import {AxiosResponse} from 'axios';

import {Coordinate, MapModeEnum} from '~/tracker/type';
import {
  CouponValueType,
  IMarketingBanner,
  RestaurantCategories,
  RestaurantGroupRestaurants,
} from '~/shared/store/models';
import {OrderSuccessData} from '~/shared/store/models/Order';

export interface IMarketingBannerRes {
  marketingBanners: IMarketingBanner[];
}

export interface IUpdateUserRes {
  redirectToOtl: boolean;
}

export interface IRestaurantGroupsRes {
  restaurantGroupList: Partial<RestaurantGroupRestaurants>[];
}

export interface IRestaurantMenuRes {
  categories: RestaurantCategories[];
}

export type IOrderRes = OrderSuccessData & {
  dishes?: OrderSuccessData['dishes'];
  orderStatus: string;
};

export type IVoucherCardResV2 = {
  balance: number;
  logo: string;
  image: string;
  description: string; // html
  amountTitle: string;
  cardNumber: string;
};

export type IRefreshTokenRes =
  | {
      accessToken: string;
      refreshToken: string;
    }
  | {
      description: string;
      code: string;
      traceId: string;
      details: {
        description: string;
        code: string;
      }[];
    };

interface LoadTenbisCreditResponse {
  code: string;
  description: string;
  details: string[];
  traceId: string;
}

export type ILoadTenbisCreditResponse = AxiosResponse | LoadTenbisCreditResponse;

export type IAutoCreditSubscriptionResponse = boolean;

export type IUnusedVouchersResponse = Array<{
  orderId: string;
  orderDate: string; // 2025-10-20T09:39:00
  amount: number;
  validTill: string; // 2025-12-31T00:00:00
}>;

export type IFetchBrandingPageDataResponse = {
  imageUrl: string;
  title: string;
  subTitle: string;
};

export type DishAdditionalInfoResponse = {
  sections: Array<{
    title: string;
    content: string;
  }>;
  disclaimer: string | null;
};

interface TimeRange {
  startTime: string;
  endTime: string;
}

interface ActivityTime {
  dayOfWeek: number;
  ranges: TimeRange[];
}

export interface IDiscountResponse {
  activityTimes: ActivityTime[];
  details: string;
  expirationDate: string;
}

export interface ApplePayMerchantSessionResponse extends MerchantSession {}
export interface TrackerContextResponse {
  pollingIntervalInMilliseconds: number;
  promisedETA: number;
  orderPlacementDateTime: number; // progress bar (to indicate 100%)
  userLocation: null | Coordinate;
  businessLocation: null | Coordinate;
  businessName: null | string;
}

export interface TrackerRouteResponse {
  eta: null | number;
  route: null | Coordinate[];
  status: null | MapModeEnum;
  durationTimeInSeconds: number;
}

export interface MoneyCard {
  id: string;
  balance: number;
  fullNumber: string;
  iconUrl: string;
  isTenbisCredit: boolean;
  isActive: boolean;
  tenbisCreditConversion: {
    availableAmount: number;
    tenbisCreditSettings: {
      autoCreditHasLimit: boolean;
      autoCreditLimitValue: number;
      isManualLoadAllowed: boolean;
      disableAutoCreditOption: boolean;
      autoCreditSubscribed: boolean;
    } | null;
  } | null;
}

export type IMoneycardsRes = MoneyCard[];

export type UserNotificationsPreferencesResponse = {
  promotionsEnabled: boolean;
  resReviewNotificationEnabled: boolean;
  pushNotificationsEnabled: boolean;
  smsNotificationsEnabled: boolean;
  emailNotificationsEnabled: boolean;
};

export const isAxiosResponse = <ApiResponse>(response: ApiResponse | AxiosResponse<ApiResponse>): response is AxiosResponse<ApiResponse> => {
  if (!response) {
    return false;
  }
  return (
    typeof response === 'object' && 'data' in response && 'statusText' in response && 'status' in response
  );
};

export type UserCreditCardResponse = {
  id: number;
  type: string;
  brand: string; // represents the credit card brand (Visa, MasterCard, etc.)
  suffix: string;
  expiration: string;
  isExpired: boolean;
};

export type UserPaypalResponse = {
  id: number;
  email: string;
};

export type UserGetPaymentMethodsResponse = {
  creditCards: UserCreditCardResponse[];
  paypal: UserPaypalResponse[];
};

export type UserCoupon = {
  id: number;
  title: string;
  expirationDate: string;
  couponsQuotaPerUser: number;
  couponsLeftPerUser: number;
  couponsDiscountBalanceLeftPerUser: number;
  valueType: CouponValueType;
};

export type FetchUserCouponsResponse = UserCoupon[];
